import React, { useState, useEffect, useRef } from 'react';
import styles from './Notes.module.css';
import { RefreshCw, MessageSquareText } from 'lucide-react';

const Notes = ({ isRecording }) => {
    const [noteContent, setNoteContent] = useState('');
    const [minimized, setMinimized] = useState(false);
    const textareaRef = useRef(null);

    // Mixed suggestions covering various professional contexts
    const suggestions = [
        // Entrepreneurship & Pitching
        'Key points for my 2-minute elevator pitch: problem, solution, market size, competitive advantage',
        'Three ways our product solves the customer pain point better than existing solutions',
        'Main investor objections to address: market size, scaling strategy, revenue model',
        "My startup's unique value proposition in one compelling sentence",
        'Key metrics to highlight: 32% MoM growth, 18% conversion rate, $127 CAC',

        // Presentation Structure
        'Opening hook → Problem statement → Solution overview → Demo → Call to action',
        'Data points to emphasize: market growth 27%, efficiency gains 43%, ROI 3.2x',
        'Three customer stories that illustrate our impact: healthcare, finance, education',
        'Agenda: market analysis (5m), product roadmap (8m), Q&A (7m)',
        'Key transitions: "This leads us to..." "Building on this idea..." "The implications are..."',

        // Public Speaking Techniques
        'Vocal variety: Emphasize "transform" and "opportunity" with stronger tone',
        'Pause for 3 seconds after revealing the key insight to let it sink in',
        'Body language: Open stance, deliberate gestures, eye contact with different sections',
        'Rhetorical techniques: Rule of three, contrast pairs, metaphor about journey',
        'Response to tough question: acknowledge, bridge, communicate key message',

        // Leadership Communication
        'Vision statement: Where we want to be in 3 years and why it matters',
        'Change management message: why we need to pivot, benefits, support available',
        'Team motivation: recent wins, upcoming challenge, confidence in our capabilities',
        'Crisis communication points: acknowledge issue, immediate actions, long-term plan',
        'Quarterly priorities: customer retention, operational efficiency, talent development',

        // Technical Explanations
        'Technical concept in simple terms: blockchain as a transparent, tamper-proof ledger',
        'System architecture explanation using the house-building metaphor',
        'Product demo sequence: login → dashboard → main feature → analytics',
        'Implementation timeline: Phase 1 (Q2), Phase 2 (Q3-Q4), Full deployment (Q1 next year)',
        'Integration benefits: 37% faster processing, seamless data flow, enhanced security',

        // Sales & Client Interactions
        'Customer objection responses: cost (ROI focus), timeline (phased approach), security',
        'Discovery questions: current challenges, ideal outcome, decision-making process',
        'Value summary: time savings, cost reduction, competitive advantage',
        'Case study highlights: 28% productivity improvement for Enterprise Client X',
        'Contract renewal talking points: achieved goals, new features, loyalty benefits',

        // Personal Professional Development
        'Career journey narrative: key pivots, lessons learned, future aspirations',
        'My leadership philosophy: empowerment, accountability, continuous learning',
        'Greatest professional challenge overcome and what it taught me',
        'Unique perspective I bring based on my diverse background in...',
        'Personal "why" that drives my work and decision-making'
    ];

    // Get a random suggestion
    const getRandomSuggestion = () => {
        const randomIndex = Math.floor(Math.random() * suggestions.length);
        return suggestions[randomIndex];
    };

    // Set initial suggestion on mount
    useEffect(() => {
        setNoteContent(getRandomSuggestion());
        // eslint-disable-next-line
    }, []);

    // Handle new suggestion request
    const handleNewSuggestion = () => {
        setNoteContent(getRandomSuggestion());
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    // Toggle minimized state
    const toggleMinimized = () => {
        setMinimized(!minimized);
    };

    return (
        <div className={`${styles.container} ${minimized ? styles.minimized : ''} ${isRecording ? styles.recording : ''}`}>
            {minimized ? (
                <div className={styles.minimizedContent} onClick={toggleMinimized}>
                    <MessageSquareText size={16} />
                </div>
            ) : (
                <>
                    <div className={styles.header}>
                        <span className={styles.title}>Notes</span>
                        <div className={styles.controls}>
                            <button
                                onClick={toggleMinimized}
                                className={styles.controlButton}
                                title="Minimize"
                            >
                                <span className={styles.minimizeIcon}>—</span>
                            </button>
                        </div>
                    </div>
                    <div className={styles.content}>
                        <textarea
                            ref={textareaRef}
                            className={styles.noteTextarea}
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                            onClick={(e) => {
                                if (e.target.value === noteContent && suggestions.includes(noteContent)) {
                                    setNoteContent('');
                                }
                            }}
                            placeholder="Type or paste your notes here..."
                        />
                        <button
                            onClick={handleNewSuggestion}
                            className={styles.refreshButton}
                            title="Get inspiration"
                        >
                            <RefreshCw size={14} />
                            <span>New suggestion</span>
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default Notes;